import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DynamicLayout from "../../components/DynamicLayout";
import { AcademyContext } from "../../context/academyContext";
import CourseHero from '../../components/CourseHero';
import FlashCardComponent from '../../components/FlashCardComponent';



// Import all the astrology lessons components here
import AriesLesson from "../lessons/astrology/signs/aries";
import TaurusLesson from "../lessons/astrology/signs/taurus";
import GeminiLesson from "../lessons/astrology/signs/gemini";
import CancerLesson from "../lessons/astrology/signs/cancer";
import LeoLesson from "../lessons/astrology/signs/leo";
import VirgoLesson from "../lessons/astrology/signs/virgo";
import LibraLesson from "../lessons/astrology/signs/libra";
import ScorpioLesson from "../lessons/astrology/signs/scorpio";
import SagittariusLesson from "../lessons/astrology/signs/sagittarius";
import CapricornLesson from "../lessons/astrology/signs/capricorn";
import AquariusLesson from "../lessons/astrology/signs/aquarius";
import PiscesLesson from "../lessons/astrology/signs/pisces";


// Import all the planet lessons components here
import SunLesson from "../lessons/astrology/planets/sun";
import MoonLesson from "../lessons/astrology/planets/moon";
import MercuryLesson from "../lessons/astrology/planets/mercury";
import VenusLesson from "../lessons/astrology/planets/venus";
import MarsLesson from "../lessons/astrology/planets/mars";
import JupiterLesson from "../lessons/astrology/planets/jupiter";
import SaturnLesson from "../lessons/astrology/planets/saturn";


// Import all the Chapter 5 lessons components here
import SignsSymbolsLesson from "../lessons/astrology/symbols/signs";
import PlanetsSymbolsLesson from "../lessons/astrology/symbols/planets";


// Import all the house lessons components here
import FirstHouseLesson from "../lessons/astrology/houses/1";
import SecondHouseLesson from "../lessons/astrology/houses/2";
import ThirdHouseLesson from "../lessons/astrology/houses/3";
import FourthHouseLesson from "../lessons/astrology/houses/4";
import FifthHouseLesson from "../lessons/astrology/houses/5";
import SixthHouseLesson from "../lessons/astrology/houses/6";
import SeventhHouseLesson from "../lessons/astrology/houses/7";
import EighthHouseLesson from "../lessons/astrology/houses/8";
import NinthHouseLesson from "../lessons/astrology/houses/9";
import TenthHouseLesson from "../lessons/astrology/houses/10";
import EleventhHouseLesson from "../lessons/astrology/houses/11";
import TwelfthHouseLesson from "../lessons/astrology/houses/12";


// Import all the Zodiac Wheel lessons components here
import StructureLesson from "../lessons/astrology/zodiacwheel/structure";
import EclipticLesson from "../lessons/astrology/zodiacwheel/eclipticvscelestial";
import HouseSystemsLesson from "../lessons/astrology/zodiacwheel/housesystems";
import SiderealTropicalLesson from "../lessons/astrology/zodiacwheel/siderealvstropical";

// Import all the Aspects lessons components here
import UnderstandingAspectsLesson from "../lessons/astrology/aspects/understanding";
import ConjunctionLesson from "../lessons/astrology/aspects/conjunction";
import OppositionLesson from "../lessons/astrology/aspects/opposition";
import TrineLesson from "../lessons/astrology/aspects/trine";
import SquareLesson from "../lessons/astrology/aspects/square";
import SextileLesson from "../lessons/astrology/aspects/sextile";
import AspectOrbsLesson from "../lessons/astrology/aspects/orbs";
import AspectPatternsLesson from "../lessons/astrology/aspects/patterns";
import PlanetaryDignitiesLesson from "../lessons/astrology/aspects/dignities";
import InterpretingAspectsLesson from "../lessons/astrology/aspects/interpretation";

// Move functions before component definition
const getMaxQuestionsForLesson = (lessonNumber) => {
  // Return the number of questions for each lesson
  // This should match the actual number of questions in each lesson
  const questionCounts = {
    1: 3, // Structure lesson has 3 questions
    2: 2, // Ecliptic lesson has 2 questions
    3: 2, // House systems lesson has 2 questions
    4: 2, // Sidereal vs Tropical lesson has 2 questions
    5: 20, // Symbols lesson has 20 questions (10 signs × 2 questions each)
    6: 12, // Planets symbols lesson has 12 questions (6 planets × 2 questions each)
    7: 3, // Aries lesson
    8: 3, // Taurus lesson
    9: 3, // Gemini lesson
    10: 3, // Cancer lesson
    11: 3, // Leo lesson
    12: 3, // Virgo lesson
    13: 3, // Libra lesson
    14: 3, // Scorpio lesson
    15: 3, // Sagittarius lesson
    16: 3, // Capricorn lesson
    17: 3, // Aquarius lesson
    18: 3, // Pisces lesson
    19: 3, // Sun lesson
    20: 3, // Moon lesson
    21: 3, // Mercury lesson
    22: 3, // Venus lesson
    23: 3, // Mars lesson
    24: 3, // Jupiter lesson
    25: 3, // Saturn lesson
    26: 3, // 1st House lesson
    27: 3, // 2nd House lesson
    28: 3, // 3rd House lesson
    29: 3, // 4th House lesson
    30: 3, // 5th House lesson
    31: 3, // 6th House lesson
    32: 3, // 7th House lesson
    33: 3, // 8th House lesson
    34: 3, // 9th House lesson
    35: 3, // 10th House lesson
    36: 3, // 11th House lesson
    37: 3, // 12th House lesson
    38: 2, // Understanding Aspects lesson
    39: 3, // Conjunction lesson
    40: 3, // Opposition lesson
    41: 3, // Trine lesson
    42: 3, // Square lesson
    43: 3, // Sextile lesson
    44: 2, // Aspect Orbs lesson
    45: 2, // Aspect Patterns lesson
    46: 3, // Planetary Dignities lesson
    47: 2, // Interpreting Aspects lesson
  };
  return questionCounts[lessonNumber] || 1;
};

const getChapterAndLessonInfo = (lessonNumber) => {
  const chapterInfo = {
    1: { chapter: "Chapter 1: The Zodiac Wheel", lesson: "Structure and Layout of the Zodiac Wheel" },
    2: { chapter: "Chapter 1: The Zodiac Wheel", lesson: "Understanding the Ecliptic and Celestial Sphere" },
    3: { chapter: "Chapter 1: The Zodiac Wheel", lesson: "House Systems: Placidus, Whole Sign, Koch, and Other Systems" },
    4: { chapter: "Chapter 1: The Zodiac Wheel", lesson: "Sidereal vs. Tropical Astrology" },
    5: { chapter: "Chapter 5: Astrological Symbols", lesson: "Sign Symbols" },
    6: { chapter: "Chapter 5: Astrological Symbols", lesson: "Planets Symbols" },
    7: { chapter: "Chapter 2: The Zodiac Signs", lesson: "Aries" },
    8: { chapter: "Chapter 2: The Zodiac Signs", lesson: "Taurus" },
    9: { chapter: "Chapter 2: The Zodiac Signs", lesson: "Gemini" },
    10: { chapter: "Chapter 2: The Zodiac Signs", lesson: "Cancer" },
    11: { chapter: "Chapter 2: The Zodiac Signs", lesson: "Leo" },
    12: { chapter: "Chapter 2: The Zodiac Signs", lesson: "Virgo" },
    13: { chapter: "Chapter 2: The Zodiac Signs", lesson: "Libra" },
    14: { chapter: "Chapter 2: The Zodiac Signs", lesson: "Scorpio" },
    15: { chapter: "Chapter 2: The Zodiac Signs", lesson: "Sagittarius" },
    16: { chapter: "Chapter 2: The Zodiac Signs", lesson: "Capricorn" },
    17: { chapter: "Chapter 2: The Zodiac Signs", lesson: "Aquarius" },
    18: { chapter: "Chapter 2: The Zodiac Signs", lesson: "Pisces" },
    19: { chapter: "Chapter 3: The Planets", lesson: "Sun" },
    20: { chapter: "Chapter 3: The Planets", lesson: "Moon" },
    21: { chapter: "Chapter 3: The Planets", lesson: "Mercury" },
    22: { chapter: "Chapter 3: The Planets", lesson: "Venus" },
    23: { chapter: "Chapter 3: The Planets", lesson: "Mars" },
    24: { chapter: "Chapter 3: The Planets", lesson: "Jupiter" },
    25: { chapter: "Chapter 3: The Planets", lesson: "Saturn" },
    26: { chapter: "Chapter 4: The Houses", lesson: "1st House" },
    27: { chapter: "Chapter 4: The Houses", lesson: "2nd House" },
    28: { chapter: "Chapter 4: The Houses", lesson: "3rd House" },
    29: { chapter: "Chapter 4: The Houses", lesson: "4th House" },
    30: { chapter: "Chapter 4: The Houses", lesson: "5th House" },
    31: { chapter: "Chapter 4: The Houses", lesson: "6th House" },
    32: { chapter: "Chapter 4: The Houses", lesson: "7th House" },
    33: { chapter: "Chapter 4: The Houses", lesson: "8th House" },
    34: { chapter: "Chapter 4: The Houses", lesson: "9th House" },
    35: { chapter: "Chapter 4: The Houses", lesson: "10th House" },
    36: { chapter: "Chapter 4: The Houses", lesson: "11th House" },
    37: { chapter: "Chapter 4: The Houses", lesson: "12th House" },
    38: { chapter: "Chapter 6: Aspects and Transits", lesson: "Understanding Aspects" },
    39: { chapter: "Chapter 6: Aspects and Transits", lesson: "Conjunction" },
    40: { chapter: "Chapter 6: Aspects and Transits", lesson: "Opposition" },
    41: { chapter: "Chapter 6: Aspects and Transits", lesson: "Trine" },
    42: { chapter: "Chapter 6: Aspects and Transits", lesson: "Square" },
    43: { chapter: "Chapter 6: Aspects and Transits", lesson: "Sextile" },
    44: { chapter: "Chapter 6: Aspects and Transits", lesson: "Aspect Orbs" },
    45: { chapter: "Chapter 6: Aspects and Transits", lesson: "Aspect Patterns" },
    46: { chapter: "Chapter 6: Aspects and Transits", lesson: "Planetary Dignities" },
    47: { chapter: "Chapter 6: Aspects and Transits", lesson: "Interpreting Aspects" },
  };
  return chapterInfo[lessonNumber] || { chapter: "Unknown Chapter", lesson: "Unknown Lesson" };
};

// 2. Sample lesson data structure (for demonstration, you should expand this for all lessons)
const lessonData = {
  // Chapter 1 Foreword
  'chapter1-foreword': {
    type: 'foreword',
    text: "Welcome to Chapter 1: The Zodiac Wheel. In this chapter, you'll learn the structure and meaning of the zodiac wheel, the foundation of all astrological interpretation. Understanding the wheel is key to reading charts and grasping the flow of cosmic energy."
  },
  // Chapter 1 Flashcard Round 1
  'chapter1-flashcards': {
    type: 'flashcards',
    deck: [
      { question: "How many signs are in the zodiac wheel?", answer: "12" },
      { question: "What is the starting point of the zodiac wheel?", answer: "Aries (0°)" },
      { question: "What is the opposite sign of Aries?", answer: "Libra" },
      { question: "What is the name for the path the Sun appears to follow through the sky?", answer: "The Ecliptic" },
      { question: "What divides the zodiac wheel into 12 equal parts?", answer: "The signs of the zodiac" }
    ]
  },
  // Lesson 1: Structure and Layout of the Zodiac Wheel
  1: {
    foreword: "The zodiac wheel is the foundation of all astrological work. Understanding its structure is essential for reading charts.",
    endWord: "Excellent! You now understand the basic structure of the zodiac wheel. This knowledge will serve as the foundation for all future astrological learning.",
    flashcardDeck: [
      { question: "How many degrees are in a complete zodiac wheel?", answer: "360 degrees" },
      { question: "How many degrees does each zodiac sign occupy?", answer: "30 degrees" },
      { question: "What is the starting point of the zodiac wheel?", answer: "0° Aries" },
      { question: "What are the four cardinal signs?", answer: "Aries, Cancer, Libra, Capricorn" },
      { question: "What are the four fixed signs?", answer: "Taurus, Leo, Scorpio, Aquarius" },
      { question: "What are the four mutable signs?", answer: "Gemini, Virgo, Sagittarius, Pisces" }
    ]
  },
  // Lesson 2: Understanding the Ecliptic and Celestial Sphere
  2: {
    foreword: "The ecliptic is the apparent path of the Sun through the sky. Understanding this concept is crucial for astrological calculations.",
    endWord: "Great work! You now understand the difference between the ecliptic and celestial sphere, and how they relate to astrological practice.",
    flashcardDeck: [
      { question: "What is the ecliptic?", answer: "The apparent path of the Sun through the sky" },
      { question: "What is the celestial sphere?", answer: "An imaginary sphere surrounding Earth with stars projected onto it" },
      { question: "What is the zodiac belt?", answer: "A band of sky extending 8-9 degrees north and south of the ecliptic" },
      { question: "Why do planets appear to move along the ecliptic?", answer: "Because the solar system is roughly flat and planets orbit in similar planes" },
      { question: "What is the difference between apparent and actual motion?", answer: "Apparent motion is how things appear from Earth, actual motion is their real movement in space" }
    ]
  },
  // Lesson 3: House Systems
  3: {
    foreword: "House systems divide the chart into 12 sections representing different areas of life. Different systems offer different perspectives.",
    endWord: "Excellent! You now understand the major house systems and their differences. This knowledge will help you choose the right system for different types of readings.",
    flashcardDeck: [
      { question: "How many houses are in an astrological chart?", answer: "12 houses" },
      { question: "What is the most popular house system in Western astrology?", answer: "Placidus" },
      { question: "What house system divides the ecliptic into equal 30-degree segments?", answer: "Equal House" },
      { question: "What is the Whole Sign house system?", answer: "Each house contains one complete sign, starting from the Ascendant" },
      { question: "What does the Ascendant represent?", answer: "The eastern horizon at the time of birth" },
      { question: "What does the Midheaven represent?", answer: "The highest point in the sky at the time of birth" }
    ]
  },
  // Lesson 4: Sidereal vs. Tropical Astrology
  4: {
    foreword: "Sidereal and Tropical astrology use different reference points for the zodiac. Understanding these differences is important for accurate chart interpretation.",
    endWord: "Perfect! You now understand the key differences between Sidereal and Tropical astrology. This knowledge will help you choose the right system for your practice.",
    flashcardDeck: [
      { question: "What is Tropical astrology based on?", answer: "The relationship between the Sun and Earth's seasons" },
      { question: "What is Sidereal astrology based on?", answer: "The actual positions of stars and constellations" },
      { question: "What is the Ayanamsa?", answer: "The difference between Tropical and Sidereal zodiacs" },
      { question: "Which system is more commonly used in Western astrology?", answer: "Tropical" },
      { question: "Which system is more commonly used in Vedic astrology?", answer: "Sidereal" },
      { question: "What causes the precession of the equinoxes?", answer: "The slow wobble of Earth's axis" }
    ]
  },
  19: { // Sun lesson as example
    chapterIntro: "Welcome to Chapter 3: The Planets. In this chapter, you will learn about the core energies that shape every chart.",
    foreword: "The Sun is the heart of the solar system and the core of the natal chart. Let's explore its meaning and influence.",
    endWord: "Great work! You now understand the Sun's role in astrology. Continue to the next lesson to deepen your planetary knowledge.",
    flashcardDeck: [
      { question: "What does the Sun represent in astrology?", answer: "Core self, ego, vitality" },
      { question: "What sign rules the Sun?", answer: "Leo" },
      { question: "What is the glyph for the Sun?", answer: "☉" }
    ]
  },
  // ...add similar objects for other lessons...
};

const AstrologyLessonPage = () => {
  const { progress, updateCourseProgress, updateQuestionProgress, getQuestionProgress } = useContext(AcademyContext);
  const [currentLesson, setCurrentLesson] = useState(progress.astrology?.completedLessons + 1 || 1);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [showHero, setShowHero] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const { level } = useParams();
  const navigate = useNavigate();

  // Define beginner course completion threshold (first 24 lessons)
  const BEGINNER_COURSE_LESSONS = 24;
  const isIntermediateUnlocked = (progress.astrology?.completedLessons || 0) >= BEGINNER_COURSE_LESSONS;

  // Memoize the max questions for current lesson
  const maxQuestions = useMemo(() => getMaxQuestionsForLesson(currentLesson), [currentLesson]);

  useEffect(() => {
    setCurrentLesson(progress.astrology?.completedLessons + 1 || 1);
    setCurrentQuestion(0);
  }, [progress.astrology?.completedLessons]);

  // Load question progress from Firebase when lesson changes
  useEffect(() => {
    const savedQuestionProgress = getQuestionProgress('astrology', currentLesson);
    if (savedQuestionProgress.length > 0) {
      // Pad the array to match maxQuestions length
      const paddedProgress = [...savedQuestionProgress];
      while (paddedProgress.length < maxQuestions) {
        paddedProgress.push(false);
      }
      setCompletedQuestions(paddedProgress);
    } else {
      setCompletedQuestions(Array(maxQuestions).fill(false));
    }
  }, [currentLesson, maxQuestions, getQuestionProgress]);

  // Memoize chapter and lesson info
  const { chapter, lesson } = useMemo(() => getChapterAndLessonInfo(currentLesson), [currentLesson]);

  // 3. Get lesson-specific data
  const lessonInfo = lessonData[currentLesson] || {};

  // Function to update question progress in Firebase
  const updateQuestionProgressInFirebase = async (questionIndex, isCompleted) => {
    await updateQuestionProgress('astrology', currentLesson, questionIndex, isCompleted);
  };

  // Enhanced setCompletedQuestions that also saves to Firebase
  const setCompletedQuestionsWithFirebase = (newCompletedQuestions) => {
    // Handle different types of input
    let finalCompletedQuestions;
    
    if (typeof newCompletedQuestions === 'function') {
      // If it's a function, call it with the current state
      finalCompletedQuestions = newCompletedQuestions(completedQuestions);
    } else if (typeof newCompletedQuestions === 'number') {
      // If it's a number, create an array with that many completed questions
      finalCompletedQuestions = Array(completedQuestions.length).fill(false);
      for (let i = 0; i < newCompletedQuestions; i++) {
        finalCompletedQuestions[i] = true;
      }
    } else if (Array.isArray(newCompletedQuestions)) {
      // If it's already an array, use it directly
      finalCompletedQuestions = newCompletedQuestions;
    } else {
      // Fallback: use current state
      finalCompletedQuestions = completedQuestions;
    }
    
    setCompletedQuestions(finalCompletedQuestions);
    
    // Save the last changed question to Firebase
    const lastChangedIndex = finalCompletedQuestions.findIndex((completed, index) => 
      completed !== completedQuestions[index]
    );
    if (lastChangedIndex !== -1) {
      updateQuestionProgressInFirebase(lastChangedIndex, finalCompletedQuestions[lastChangedIndex]);
    }
  };

  const renderLesson = () => {
    switch (currentLesson) {
      // Reordered lessons based on your specification
      case 1:
        return <StructureLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 2:
        return <EclipticLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 3:
        return <HouseSystemsLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 4:
        return <SiderealTropicalLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 5:
        return <SignsSymbolsLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 6:
        return <PlanetsSymbolsLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      
      // Zodiac Signs
      case 7:
        return <AriesLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 8:
        return <TaurusLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 9:
        return <GeminiLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 10:
        return <CancerLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 11:
        return <LeoLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 12:
        return <VirgoLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 13:
        return <LibraLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 14:
        return <ScorpioLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 15:
        return <SagittariusLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 16:
        return <CapricornLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 17:
        return <AquariusLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 18:
        return <PiscesLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      
      // Planets
      case 19:
        return <SunLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 20:
        return <MoonLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 21:
        return <MercuryLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 22:
        return <VenusLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 23:
        return <MarsLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 24:
        return <JupiterLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 25:
        return <SaturnLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      
      // Houses
      case 26:
        return <FirstHouseLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 27:
        return <SecondHouseLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 28:
        return <ThirdHouseLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 29:
        return <FourthHouseLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 30:
        return <FifthHouseLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 31:
        return <SixthHouseLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 32:
        return <SeventhHouseLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 33:
        return <EighthHouseLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 34:
        return <NinthHouseLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 35:
        return <TenthHouseLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 36:
        return <EleventhHouseLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 37:
        return <TwelfthHouseLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      
      // Aspects
      case 38:
        return <UnderstandingAspectsLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 39:
        return <ConjunctionLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 40:
        return <OppositionLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 41:
        return <TrineLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 42:
        return <SquareLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 43:
        return <SextileLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 44:
        return <AspectOrbsLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 45:
        return <AspectPatternsLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 46:
        return <PlanetaryDignitiesLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      case 47:
        return <InterpretingAspectsLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
      
      default:
        return <StructureLesson level={level} onNextLesson={handleNext} markLessonComplete={markLessonComplete} currentQuestion={currentQuestion} onQuestionChange={setCurrentQuestion} currentLesson={currentLesson} completedQuestions={completedQuestions} setCompletedQuestions={setCompletedQuestionsWithFirebase} />;
    }
  };

  const handleNext = () => {
    // Navigate to next lesson when lesson is completed
    if (currentLesson < 48) {
      setCurrentLesson(currentLesson + 1);
      setCurrentQuestion(0);
    }
  };

  const handleNextQuestion = () => {
    // Navigate to next question within current lesson
    const maxQuestions = getMaxQuestionsForLesson(currentLesson);
    if (currentQuestion < maxQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePreviousQuestion = () => {
    // Navigate to previous question within current lesson
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const markLessonComplete = async () => {
    await updateCourseProgress('astrology', Math.max(progress.astrology?.completedLessons || 0, currentLesson));
  };

  const handleLessonChange = (event) => {
    const value = event.target.value;
    if (value.startsWith('flashcards-')) {
      setCurrentLesson(value);
      setCurrentQuestion(0);
    } else {
      setCurrentLesson(Number(value));
      setCurrentQuestion(0);
    }
  };

  const handleLevelChange = (newLevel) => {
    navigate(`/academy/${newLevel}/lesson?lesson=${currentLesson}`);
  };

  const toggleHero = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setShowHero(!showHero);
    
    // Reset animation state after animation completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  function getFlashcardDeckForLesson(flashcardKey) {
    // flashcardKey is like 'flashcards-1', 'flashcards-7', etc.
    // You should have a mapping or logic to return the correct deck for each lesson.
    // For now, return a placeholder deck.
    const lessonNum = Number(flashcardKey.replace('flashcards-', ''));
    // Example: you could have a flashcardDecks object or use lessonData[lessonNum].flashcardDeck
    if (lessonData[lessonNum] && lessonData[lessonNum].flashcardDeck) {
      return lessonData[lessonNum].flashcardDeck;
    }
    // Fallback: return an empty array or a default deck
    return [];
  }

  return (
    <DynamicLayout>
      {/* Chapter Intro (only at the start of a chapter) */}
      {/* Foreword */}
      {/* End Word */}
      <CourseHero
        courseTitle="Astrology Course"
        level={level}
        chapter={chapter}
        lesson={lesson}
        currentLesson={currentLesson}
        completedLessons={progress.astrology?.completedLessons || 0}
        totalLessons={48}
        completedQuestions={completedQuestions}
        maxQuestions={getMaxQuestionsForLesson(currentLesson)}
        lessonOptions={
          <>
            <optgroup label="Chapter 1: The Zodiac Wheel">
              <option value="chapter1-foreword">Chapter Foreword</option>
              <option value={1}>Structure Lesson</option>
              <option value="flashcards-1">Flashcards 1</option>
              <option value={2}>Ecliptic Lesson</option>
              <option value="flashcards-2">Flashcards 2</option>
              <option value={3}>House Systems Lesson</option>
              <option value="flashcards-3">Flashcards 3</option>
              <option value={4}>Sidereal vs. Tropical Astrology Lesson</option>
              <option value="flashcards-4">Flashcards 4</option>
            </optgroup>
            <optgroup label="Chapter 2: The Zodiac Signs">
              <option value="chapter2-foreword">Chapter Foreword</option>
              <option value={7}>Aries Lesson</option>
              <option value="flashcards-7">Flashcards 7</option>
              <option value={8}>Taurus Lesson</option>
              <option value="flashcards-8">Flashcards 8</option>
              <option value={9}>Gemini Lesson</option>
              <option value="flashcards-9">Flashcards 9</option>
              <option value={10}>Cancer Lesson</option>
              <option value="flashcards-10">Flashcards 10</option>
              <option value={11}>Leo Lesson</option>
              <option value="flashcards-11">Flashcards 11</option>
              <option value={12}>Virgo Lesson</option>
              <option value="flashcards-12">Flashcards 12</option>
              <option value={13}>Libra Lesson</option>
              <option value="flashcards-13">Flashcards 13</option>
              <option value={14}>Scorpio Lesson</option>
              <option value="flashcards-14">Flashcards 14</option>
              <option value={15}>Sagittarius Lesson</option>
              <option value="flashcards-15">Flashcards 15</option>
              <option value={16}>Capricorn Lesson</option>
              <option value="flashcards-16">Flashcards 16</option>
              <option value={17}>Aquarius Lesson</option>
              <option value="flashcards-17">Flashcards 17</option>
              <option value={18}>Pisces Lesson</option>
              <option value="flashcards-18">Flashcards 18</option>
            </optgroup>
            <optgroup label="Chapter 3: The Planets">
              <option value="chapter3-foreword">Chapter Foreword</option>
              <option value={19}>Sun Lesson</option>
              <option value="flashcards-19">Flashcards 19</option>
              <option value={20}>Moon Lesson</option>
              <option value="flashcards-20">Flashcards 20</option>
              <option value={21}>Mercury Lesson</option>
              <option value="flashcards-21">Flashcards 21</option>
              <option value={22}>Venus Lesson</option>
              <option value="flashcards-22">Flashcards 22</option>
              <option value={23}>Mars Lesson</option>
              <option value="flashcards-23">Flashcards 23</option>
              <option value={24}>Jupiter Lesson</option>
              <option value="flashcards-24">Flashcards 24</option>
              <option value={25}>Saturn Lesson</option>
              <option value="flashcards-25">Flashcards 25</option>
            </optgroup>
            <optgroup label="Chapter 4: The Houses">
              <option value="chapter4-foreword">Chapter Foreword</option>
              <option value={26}>1st House Lesson</option>
              <option value="flashcards-26">Flashcards 26</option>
              <option value={27}>2nd House Lesson</option>
              <option value="flashcards-27">Flashcards 27</option>
              <option value={28}>3rd House Lesson</option>
              <option value="flashcards-28">Flashcards 28</option>
              <option value={29}>4th House Lesson</option>
              <option value="flashcards-29">Flashcards 29</option>
              <option value={30}>5th House Lesson</option>
              <option value="flashcards-30">Flashcards 30</option>
              <option value={31}>6th House Lesson</option>
              <option value="flashcards-31">Flashcards 31</option>
              <option value={32}>7th House Lesson</option>
              <option value="flashcards-32">Flashcards 32</option>
              <option value={33}>8th House Lesson</option>
              <option value="flashcards-33">Flashcards 33</option>
              <option value={34}>9th House Lesson</option>
              <option value="flashcards-34">Flashcards 34</option>
              <option value={35}>10th House Lesson</option>
              <option value="flashcards-35">Flashcards 35</option>
              <option value={36}>11th House Lesson</option>
              <option value="flashcards-36">Flashcards 36</option>
              <option value={37}>12th House Lesson</option>
              <option value="flashcards-37">Flashcards 37</option>
            </optgroup>
            <optgroup label="Chapter 5: Astrological Symbols">
              <option value="chapter5-foreword">Chapter Foreword</option>
              <option value={5}>Sign Symbols Lesson</option>
              <option value="flashcards-5">Flashcards 5</option>
              <option value={6}>Planets Symbols Lesson</option>
              <option value="flashcards-6">Flashcards 6</option>
            </optgroup>
            <optgroup label="Chapter 6: Aspects and Transits">
              <option value="chapter6-foreword">Chapter Foreword</option>
              <option value={38}>Understanding Aspects Lesson</option>
              <option value="flashcards-38">Flashcards 38</option>
              <option value={39}>Conjunction Lesson</option>
              <option value="flashcards-39">Flashcards 39</option>
              <option value={40}>Opposition Lesson</option>
              <option value="flashcards-40">Flashcards 40</option>
              <option value={41}>Trine Lesson</option>
              <option value="flashcards-41">Flashcards 41</option>
              <option value={42}>Square Lesson</option>
              <option value="flashcards-42">Flashcards 42</option>
              <option value={43}>Sextile Lesson</option>
              <option value="flashcards-43">Flashcards 43</option>
              <option value={44}>Aspect Orbs Lesson</option>
              <option value="flashcards-44">Flashcards 44</option>
              <option value={45}>Aspect Patterns Lesson</option>
              <option value="flashcards-45">Flashcards 45</option>
              <option value={46}>Planetary Dignities Lesson</option>
              <option value="flashcards-46">Flashcards 46</option>
              <option value={47}>Interpreting Aspects Lesson</option>
              <option value="flashcards-47">Flashcards 47</option>
            </optgroup>
          </>
        }
        onLessonChange={handleLessonChange}
        onLevelChange={handleLevelChange}
        isIntermediateUnlocked={isIntermediateUnlocked}
        showHero={showHero}
        isAnimating={isAnimating}
        onToggleHero={toggleHero}
        currentQuestion={currentQuestion}
        onQuestionChange={setCurrentQuestion}
        totalQuestions={getMaxQuestionsForLesson(currentLesson)}
      />
      
      <div className="full-screen-image-container hasbackgroundblack margin-top-0 nomaxheight has-text-centered">
        <section className="container has-text-centered">
          {renderLesson()}
        </section>
      </div>
      {/* Flashcards (after reading, before quiz) */}
      {typeof currentLesson === 'string' && currentLesson.startsWith('flashcards-') && (
        <section className="section has-text-centered">
          <div className="container content">
            <h3 className="has-text-primary">Flashcard Review</h3>
            <FlashCardComponent deck={getFlashcardDeckForLesson(currentLesson)} />
          </div>
        </section>
      )}
      {/* End Word */}
    </DynamicLayout>
  );
};

export default AstrologyLessonPage;
